import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reportAPI } from '../../services/api';
import { Card } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import './AdminDashboard.css';

const MONTHS    = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const PIE_COLS  = ['#e63946','#14b8a6','#f59e0b','#3b82f6','#8b5cf6','#10b981'];

const KpiCard = ({ label, value, icon, color, to }) => (
  <Link to={to || '#'} className="kpi-card" style={{ '--kpi-color': color }}>
    <div className="kpi-icon">{icon}</div>
    <div className="kpi-body">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{value}</span>
    </div>
  </Link>
);

const AdminDashboard = () => {
  const { user }          = useAuth();
  const [data, setData]   = useState(null);
  const [year, setYear]   = useState(new Date().getFullYear());
  const [loading, setL]   = useState(true);

  useEffect(() => {
    setL(true);
    reportAPI.dashboard({ year })
      .then(({ data: r }) => setData(r.data))
      .catch(() => {})
      .finally(() => setL(false));
  }, [year]);

  const revenueData = data?.monthly_revenue
    ? Object.entries(data.monthly_revenue).map(([m, v]) => ({
        month:   MONTHS[+m - 1],
        revenue: parseFloat(v.revenue || 0),
        count:   parseInt(v.count   || 0),
      }))
    : [];

  const pieData = data?.quote_statuses
    ? Object.entries(data.quote_statuses)
        .map(([s, c]) => ({
          name:  { pending:'En attente', sent:'Envoyé', accepted:'Accepté',
                   rejected:'Refusé', converted:'Converti', expired:'Expiré' }[s] || s,
          value: parseInt(c),
        }))
        .filter(d => d.value > 0)
    : [];

  const k = data?.kpis || {};

  return (
    <div className="admin-dashboard fade-in">
      {/* ── Header ── */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Tableau de bord</h1>
          <p className="dashboard-subtitle">Bonjour {user?.first_name}</p>
        </div>
        <select value={year} onChange={e => setYear(+e.target.value)} className="form-input form-select" style={{ width: 110 }}>
          {[0,1,2].map(i => { const y = new Date().getFullYear()-i; return <option key={y} value={y}>{y}</option>; })}
        </select>
      </div>

      {loading ? (
        <div className="page-loader"><div className="spinner spinner-lg" /></div>
      ) : (
        <>
          {/* ── KPIs ── */}
          <div className="kpi-grid">
            <KpiCard label="Nouvelles commandes" value={k.orders_new ?? 0}    to="/admin/orders"   color="#e63946"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>}
            />
            <KpiCard label="Devis en attente"    value={k.quotes_pending ?? 0} to="/admin/quotes"  color="#f59e0b"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>}
            />
            <KpiCard label="Factures en retard"  value={k.invoices_overdue ?? 0} to="/admin/invoices" color="#ef4444"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
            />
            <KpiCard label="Clients actifs"      value={k.total_clients ?? 0} to="/admin/clients"  color="#14b8a6"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>}
            />
            <KpiCard label={`CA ${year} HT`}
              value={`${parseFloat(k.revenue_ytd||0).toLocaleString('fr-FR',{minimumFractionDigits:0})} €`}
              to="/admin/invoices" color="#3b82f6"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 7A9 9 0 1 0 18 17"/><line x1="5" y1="11" x2="14" y2="11"/><line x1="5" y1="14" x2="14" y2="14"/></svg>}
            />
            <KpiCard label="Messages non lus"    value={k.messages_unread ?? 0} to="/admin/messages" color="#8b5cf6"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
            />
          </div>

          {/* ── Charts ── */}
          <div className="charts-row">
            <Card className="chart-card" padding="md">
              <h3 className="chart-title">Chiffre d'affaires mensuel {year}</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={revenueData} margin={{ top:10, right:10, left:0, bottom:0 }}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#e63946" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#e63946" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="month" tick={{ fontSize:11, fill:'#94a3b8' }}/>
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(1).replace('.0','')}k€`:`${v} €`} width={60}/>
                  <Tooltip formatter={v=>[`${parseFloat(v).toLocaleString('fr-FR',{minimumFractionDigits:2})} €`,'CA HT']}
                    contentStyle={{ borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }}/>
                  <Area type="monotone" dataKey="revenue" stroke="#e63946" strokeWidth={2.5} fill="url(#g1)"/>
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className="chart-card chart-card-sm" padding="md">
              <h3 className="chart-title">Statuts des devis</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                      {pieData.map((_,i) => <Cell key={i} fill={PIE_COLS[i % PIE_COLS.length]}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius:8, fontSize:12 }}/>
                    <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize:12 }}/>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="chart-empty">Aucun devis pour l'instant</div>
              )}
            </Card>
          </div>

          {/* ── Bottom ── */}
          <div className="bottom-row">
            {data?.top_products?.length > 0 && (
              <Card className="bottom-card" padding="none">
                <div className="card-header-inner"><h3>Top produits commandés</h3></div>
                <table className="data-table">
                  <thead><tr><th>Produit</th><th>Commandes</th><th>Valeur est.</th></tr></thead>
                  <tbody>
                    {data.top_products.map((p,i) => (
                      <tr key={i}>
                        <td>{p.product_name || '—'}</td>
                        <td>{p.order_count}</td>
                        <td>{parseFloat(p.total_value||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {data?.recent_activity?.length > 0 && (
              <Card className="bottom-card" padding="none">
                <div className="card-header-inner"><h3>Activité récente</h3></div>
                <div className="activity-list">
                  {data.recent_activity.map((a,i) => (
                    <div key={i} className="activity-item">
                      <div className={`activity-dot dot-${a.type}`}/>
                      <div className="activity-body">
                        <span className="activity-type">{a.type === 'order' ? 'Commande' : 'Devis'}</span>
                        <span className="activity-ref">{a.reference}</span>
                        <span className="activity-name">{a.name}</span>
                      </div>
                      <span className="activity-time">{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;